import {
  Create,
  Form,
  Image,
  Input,
  Select,
  Space,
  Typography,
  useForm,
  useSelect,
} from '@pankod/refine-antd'
import { useNavigation, useOne, useResource } from '@pankod/refine-core'
import { config } from 'config'
import { Resources, ValidationRules } from 'consts'
import { useQuery, useSiteContextHelper } from 'hooks'
import { PaymentSystem, SharedPaymentSystem } from 'interfaces'
import { useEffect } from 'react'

const { Text } = Typography

export const PaymentSystemsCreate: React.FC = () => {
  const { siteId, langId, isEnabled, siteSlug } = useSiteContextHelper()

  const { list } = useNavigation()
  const { resource } = useResource()
  const query = useQuery()

  const sharedPSID = query.get(Resources.SHARED_PAYMENT_SYSTEMS)

  const { formProps, saveButtonProps, form } = useForm<PaymentSystem>({
    resource: Resources.PAYMENT_SYSTEMS,
    redirect: false,
    onMutationSuccess() {
      list(resource.route)
    },
    queryOptions: {
      enabled: false,
    },
  })

  const { data, isLoading, status } = useOne<SharedPaymentSystem>({
    id: sharedPSID!,
    resource: Resources.SHARED_PAYMENT_SYSTEMS,
    metaData: {
      populate: ['logo'],
    },
    queryOptions: {
      enabled: isEnabled && !!sharedPSID,
    },
  })

  const { selectProps, queryResult } = useSelect<SharedPaymentSystem>({
    resource: Resources.SHARED_PAYMENT_SYSTEMS,
    metaData: {
      populate: ['logo'],
    },
    sort: [
      {
        field: 'createdAt',
        order: 'desc',
      },
    ],
    queryOptions: {
      enabled: isEnabled && !sharedPSID,
    },
  })

  const onSharedPaymentSystemChange = (value: any) => {
    const val = form.getFieldValue(['shared_payment_system', 'id'])
    const item = records?.find((r) => r.id === val)

    if (item) {
      form.setFieldsValue({
        slug: `${item.slug}-${siteSlug}`,
      })
    }
  }

  const records = queryResult.data?.data

  const oneRecord = data?.data

  useEffect(() => {
    if (status === 'success' && oneRecord) {
      form.setFieldsValue({
        slug: `${oneRecord.slug}-${siteSlug}`,
      })
    }
  }, [status, form, oneRecord, siteSlug])

  return (
    <Create
      saveButtonProps={saveButtonProps}
      title="Create Payment System Page"
      isLoading={!!(isLoading && sharedPSID)}
    >
      <Form {...formProps} key={`form-${siteId}-${langId}}`} layout="vertical">
        <Form.Item
          label="Meta title"
          name={['meta', 'title']}
          rules={ValidationRules.META_TITLE}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label="Meta description"
          name={['meta', 'description']}
          rules={ValidationRules.META_DESCRIPTION}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label="Shared Payment System"
          name={['shared_payment_system', 'id']}
          initialValue={sharedPSID ? Number(sharedPSID) : undefined}
          rules={[{ required: true, type: 'number' }]}
        >
          {!sharedPSID ? (
            <Select
              {...selectProps}
              options={undefined}
              optionLabelProp="label"
              onChange={onSharedPaymentSystemChange}
            >
              {records?.map((item) => {
                return (
                  <Select.Option
                    key={item.id}
                    value={item.id}
                    label={item.title}
                  >
                    <Space align="center">
                      <Image
                        width={64}
                        height={64}
                        style={{ objectFit: 'contain' }}
                        src={config.API_URL + item?.logo?.url}
                      />
                      <Text>{item.title}</Text>
                    </Space>
                  </Select.Option>
                )
              })}
            </Select>
          ) : (
            <Space align="center">
              <Image
                width={64}
                height={64}
                style={{ objectFit: 'contain' }}
                src={config.API_URL + oneRecord?.logo?.url!}
              />
              <Text>{oneRecord?.title}</Text>
            </Space>
          )}
        </Form.Item>
        <Form.Item label="Slug" name="slug" rules={[{ required: true }]}>
          <Input disabled placeholder="Depends on shared payment system" />
        </Form.Item>
        <Form.Item
          name={['site', 'id']}
          rules={[{ required: true, type: 'number' }]}
          initialValue={siteId}
          label="Site id"
        >
          <Input disabled />
        </Form.Item>
        <Form.Item
          name={['language', 'id']}
          rules={[{ required: true, type: 'number' }]}
          initialValue={langId}
          label="Language id"
        >
          <Input disabled />
        </Form.Item>
      </Form>
    </Create>
  )
}
